import { useMemo, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { GET_SUBSCRIPTION_STATUS } from "../../graphql/queries";
import { summarizeSubscription } from "../../utils/subscription";
import UpgradeModal from "../../components/UpgradeModal";
import IntegrationRequiredState from "../../components/IntegrationRequiredState";
import {
  BarChart3,
  ClipboardList,
  Clock,
  Globe,
  Lightbulb,
  Megaphone,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Search,
  ShieldCheck,
  Calendar,
  ArrowRight,
  DollarSign,
  Music2,
} from "lucide-react";
import pageStyles from "../../styles/PageLayout.module.css";
import { collectionByKey } from "./reportViewUtils";
import styles from "./ReportsPage.module.css";

// Report types in display order
const reportsOrder = [
  "benchmark",
  "meta",
  "google_search_console",
  "google_merchant_center",
  "revenue_reality",
  // "historical_orders",
  "market",
  "target_audience",
  "competitor",
  "yearlyContent",
  // "ideation",
];

// Report labels
const labels = {
  benchmark: "Shop Summary",
  market: "Shop Market Analysis",
  market_v2: "Shop Market Analysis V2",
  competitor: "Shop Competitor Analysis",
  ideation: "Monthly Ideation Report",
  seo: "Shop SEO Report",
  adsAudit: "Ads Audit Report",
  influencer: "Influencer & Trending Report",
  meta: "Meta Ads Report",
  google_search_console: "Google Search Console",
  google_merchant_center: "Google Merchant Center",
  revenue_reality: "Revenue Reality Report",
  yearlyContent: "Monthly Events Calendar",
  target_audience: "Target Audience",
  // historical_orders: "Historical Orders Report",
};

const FREE_ACCESS_REPORT_KEYS = new Set([
  "benchmark",
  "meta",
  "google_search_console",
  "google_merchant_center",
  "revenue_reality",
]);

// ─── Explore → Chat helpers ────────────────────────────────────────────────

const _asText = (value) => {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object") {
    if (typeof value.text === "string") return value.text.trim();
    if (typeof value.report_body === "string") return value.report_body.trim();
    if (Array.isArray(value.report_body))
      return value.report_body.filter(Boolean).join("\n\n").trim();
  }
  return "";
};

const _extractAiBlock = (ai) => {
  if (!ai || typeof ai !== "object") return "";
  const toList = (val) =>
    Array.isArray(val) ? val : typeof val === "string" && val ? [val] : [];
  const parts = [];
  const overview = toList(ai.overview);
  const insights = toList(ai.insights);
  const recs = toList(ai.recommendations);
  if (overview.length)
    parts.push("Overview:\n" + overview.map((s) => `• ${s}`).join("\n"));
  if (insights.length)
    parts.push("Insights:\n" + insights.map((s) => `• ${s}`).join("\n"));
  if (recs.length)
    parts.push("Recommendations:\n" + recs.map((s) => `• ${s}`).join("\n"));
  return parts.join("\n\n");
};

const buildReportChatContent = (report, label) => {
  const data = report?.data || {};
  const col = data?.collection_name;
  const parts = [`Here is my ${label} report data:\n`];

  switch (col) {
    case "shop_benchmark_report": {
      if (data.summary) parts.push(data.summary);
      const sales = data.shopify_sales;
      if (sales) {
        const lines = [];
        if (sales.total_sales != null) lines.push(`Total Sales: ${sales.total_sales}`);
        if (sales.total_orders != null) lines.push(`Total Orders: ${sales.total_orders}`);
        if (lines.length) parts.push("Sales Metrics:\n" + lines.join("\n"));
      }
      const orders = data.orders;
      if (orders) {
        const lines = [];
        if (orders.avg_order_value != null)
          lines.push(`Avg Order Value: ${orders.avg_order_value}`);
        if (orders.total_orders != null)
          lines.push(`Total Orders: ${orders.total_orders}`);
        if (lines.length) parts.push("Order Metrics:\n" + lines.join("\n"));
      }
      break;
    }
    case "shop_target_audience": {
      const payload = data?.data || data;
      if (payload.summary) parts.push(payload.summary);
      const profiles = Array.isArray(payload.icp_profiles)
        ? payload.icp_profiles.slice(0, 3)
        : [];
      profiles.forEach((p, i) => {
        const pParts = [
          `Audience Profile ${i + 1}: ${p.name || p.type || "Unnamed"}`,
        ];
        if (p.bio) pParts.push(p.bio);
        if (p.age_range) pParts.push(`Age Range: ${p.age_range}`);
        if (
          Array.isArray(p.pain_points_transactional) &&
          p.pain_points_transactional.length
        )
          pParts.push(
            "Pain Points: " +
              p.pain_points_transactional.slice(0, 3).join(", "),
          );
        parts.push(pParts.join("\n"));
      });
      break;
    }
    case "shop_market_analysis": {
      if (data.summary) parts.push(data.summary);
      const ai = _extractAiBlock(data.ai_analysis);
      if (ai) parts.push(ai);
      break;
    }
    case "shop_competitor_analysis": {
      if (data.summary) parts.push(data.summary);
      const competitors = Array.isArray(data.competitors)
        ? data.competitors.slice(0, 5)
        : [];
      if (competitors.length)
        parts.push(
          "Top Competitors:\n" +
            competitors
              .map(
                (c) =>
                  `• ${c.domain || c.name || c.normalized_domain || "Unknown"}`,
              )
              .join("\n"),
        );
      break;
    }
    case "shop_meta": {
      const ai =
        data?.ai_analysis || data?._ai_analysis || {};
      const aiText = _extractAiBlock(ai);
      if (aiText) parts.push(aiText);
      if (data.summary) parts.push(data.summary);
      break;
    }
    case "google_search_console": {
      if (data.summary) parts.push(`Summary: ${data.summary}`);
      ["indexing", "core_web_vitals", "performance"].forEach((key) => {
        const text = _asText(data[key]);
        if (text)
          parts.push(
            `${key.replace(/_/g, " ").toUpperCase()}:\n${text.slice(0, 600)}`,
          );
      });
      break;
    }
    case "google_merchant_center": {
      if (data.summary) parts.push(data.summary);
      const ai = _extractAiBlock(data.ai_analysis);
      if (ai) parts.push(ai);
      if (data.total_products != null)
        parts.push(`Total Products: ${data.total_products}`);
      break;
    }
    case "revenue_reality_full_report": {
      const mainText = _asText(data.revenue_reality);
      if (mainText)
        parts.push(`Revenue Reality:\n${mainText.slice(0, 800)}`);
      if (data.summary) parts.push(data.summary);
      break;
    }
    case "yearly_content_calendar": {
      if (data.summary) parts.push(data.summary);
      const months = data.months || data.calendar || {};
      const monthNames = Object.keys(months);
      if (monthNames.length)
        parts.push(`Calendar covers: ${monthNames.join(", ")}`);
      break;
    }
    default: {
      if (data.summary) parts.push(data.summary);
      const ai = _extractAiBlock(data.ai_analysis);
      if (ai) parts.push(ai);
      break;
    }
  }

  return parts.filter(Boolean).join("\n\n");
};

// Icons per tile
const iconByKey = {
  benchmark: { Icon: BarChart3 },
  market: { Icon: Globe },
  competitor: { Icon: Users },
  ideation: { Icon: Lightbulb },
  seo: { Icon: Search },
  adsAudit: { Icon: Megaphone },
  influencer: { Icon: TrendingUp },
  meta: { Icon: Smartphone },
  google_search_console: { Icon: Search },
  google_merchant_center: { Icon: ShieldCheck },
  revenue_reality: { Icon: DollarSign },
  yearlyContent: { Icon: Calendar },
  target_audience: { Icon: Target },
  historical_orders: { Icon: ClipboardList },
};

const ReportsPage = () => {
  const { user } = useAuthUser();
  const { shopData } = useShop();
  const { t, locale } = useLocalization();
  const isArabic = locale === "ar";
  const location = useLocation();
  const navigate = useNavigate();
  const authToken = user?.token;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const { data: subData } = useQuery(GET_SUBSCRIPTION_STATUS, {
    skip: !authToken,
    fetchPolicy: "cache-and-network",
    context: { headers },
  });
  const subSummary = useMemo(
    () => summarizeSubscription(subData?.getSubscriptionStatus),
    [subData],
  );
  const hasShopifyConnection = Boolean(
    user?.shopifyToken ||
      user?.shop ||
      shopData?._id ||
      shopData?.id ||
      shopData?.myshopifyDomain ||
      (Array.isArray(user?.authProviders) &&
        user.authProviders.some((item) => item?.provider === "shopify")),
  );

  const [upgradeModalReport, setUpgradeModalReport] = useState(null);

  // Clear any lingering focus state passed via navigation.
  useEffect(() => {
    if (!location?.state) return;
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  // Temporarily disabled to remove expensive getCurrentReportsByUserId traffic.
  // const { data, loading, error } = useQuery(GET_CURRENT_REPORTS_BY_USER, {
  //   skip: !userId || !authToken,
  //   variables: { userId, ...(shopId ? { shopId } : {}) },
  //   context: { headers },
  //   fetchPolicy: "cache-first",
  //   nextFetchPolicy: "cache-first",
  // });

  const reports = useMemo(() => [], []);
  const loading = false;
  const error = null;

  // Index reports by collection_name (keep newest per collection)
  const reportByCollection = useMemo(() => {
    const idx = {};
    for (const r of reports) {
      const col = r?.data?.collection_name;
      if (!col) continue;
      const current = idx[col];
      if (!current || new Date(r.createdAt) > new Date(current.createdAt)) {
        idx[col] = r;
      }
    }
    return idx;
  }, [reports]);

  const onPressReport = useCallback(
    (key) => {
      const canAccessReport =
        subSummary.isMonthlySubscription || FREE_ACCESS_REPORT_KEYS.has(key);

      if (!canAccessReport) {
        setUpgradeModalReport(key);
        return;
      }

      const col = collectionByKey[key];
      const report = reportByCollection[col];
      if (!report) return;
      navigate(`/reports/${report?._id || report?.id}`);
    },
    [navigate, reportByCollection, subSummary.isMonthlySubscription],
  );

  const handleExplore = useCallback(
    (key, e) => {
      e?.stopPropagation();
      const col = collectionByKey[key];
      const report = reportByCollection[col];
      if (!report) return;
      const label = labels[key] || key;
      const content = buildReportChatContent(report, label);
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-seed", { detail: { content } }),
      );
    },
    [reportByCollection],
  );

  // Intentionally skip auto-opening a modal based on navigation state.

  /* Removing as it is not used currently
  const formatDate = (d) => {
    try {
      const date = new Date(d);
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(d || "");
    }
  };
  */

  const reportLabels = {
    benchmark: t("ReportsPage.labels.benchmark") || labels.benchmark,
    market: t("ReportsPage.labels.market") || labels.market,
    competitor: t("ReportsPage.labels.competitor") || labels.competitor,
    ideation: t("ReportsPage.labels.ideation") || labels.ideation,
    seo: t("ReportsPage.labels.seo") || labels.seo,
    adsAudit: t("ReportsPage.labels.adsAudit") || labels.adsAudit,
    influencer: t("ReportsPage.labels.influencer") || labels.influencer,
    meta: t("ReportsPage.labels.meta") || labels.meta,
    google_search_console:
      t("ReportsPage.labels.google_search_console") ||
      labels.google_search_console,
    google_merchant_center:
      t("ReportsPage.labels.google_merchant_center") ||
      labels.google_merchant_center,
    revenue_reality:
      t("ReportsPage.labels.revenue_reality") ||
      labels.revenue_reality,
    yearlyContent:
      t("ReportsPage.labels.yearlyContent") || labels.yearlyContent,
    target_audience:
      t("ReportsPage.labels.target_audience") || labels.target_audience,
    historical_orders:
      t("ReportsPage.labels.historical_orders") || labels.historical_orders,
  };

  if (!hasShopifyConnection) {
    return (
      <section
        className={`${pageStyles.page} ${styles.wrapper} ${isArabic ? styles.rtl : ""}`}
        data-page="reports"
      >
        <IntegrationRequiredState
          eyebrow="Connect Shopify to unlock reports"
          title="Zeqi needs your commerce data before reports and assessments can light up."
          body="Connect your Shopify store in Integrations so I can analyze orders, products, revenue, customer behavior, and conversion patterns."
          buttonLabel="Open integrations"
          onClick={() => navigate("/integrations")}
        />
      </section>
    );
  }

  return (
    <section
      className={`${pageStyles.page} ${styles.wrapper} ${isArabic ? styles.rtl : ""}`}
      data-page="reports"
    >
      {loading && reports.length === 0 && (
        <p className={styles.loading}>
          {t("ReportsPage.loading") || "Loading reports..."}
        </p>
      )}
      {error && (
        <p className={styles.loading}>
          {t("ReportsPage.loadErrorPrefix") || "Failed to load reports:"}{" "}
          {error.message}
        </p>
      )}

      <ul className={styles.reportGrid}>
        {reportsOrder.map((key) => {
          const icon = iconByKey[key] || {};
          const Icon = icon.Icon || BarChart3;
          const existing = reportByCollection[collectionByKey[key]];
          const canAccessReport =
            subSummary.isMonthlySubscription || FREE_ACCESS_REPORT_KEYS.has(key);
          const isLocked = !existing && !canAccessReport;
          const isGenerating = !existing && canAccessReport;
          const pressable = !!existing || isLocked;

          return (
            <li
              key={key}
              className={`${styles.reportItem} ${styles[`reportItem_${key}`] || ""}`}
            >
              <button
                className={`${styles.reportTile} ${
                  isGenerating ? styles.reportTileDisabled : ""
                } ${isLocked ? styles.reportTileLocked : ""}`}
                onClick={() => pressable && onPressReport(key)}
                disabled={isGenerating}
              >
                <div className={styles.reportContent}>
                  <span className={styles.reportTitle}>
                    {reportLabels[key]}
                  </span>
                  {existing ? (
                    <div className={styles.reportActions}>
                      <span className={styles.actionButton} aria-hidden="true">
                        <ArrowRight className={styles.actionIcon} size={16} />
                      </span>
                      <span
                        className={styles.explorePill}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleExplore(key, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            handleExplore(key, e);
                        }}
                      >
                        {t("ReportsPage.explore") || "Explore"}
                      </span>
                      <span className={styles.reportBadge} aria-hidden="true">
                        <Icon
                          className={styles.badgeIcon}
                          size={18}
                          strokeWidth={2.1}
                        />
                      </span>
                    </div>
                  ) : isGenerating ? (
                    <span className={styles.reportHint}>
                      {t("ReportsPage.generating") ||
                        "Generating. You will be notified."}
                    </span>
                  ) : isLocked ? (
                    <span className={styles.reportLockedHint}>
                      Monthly plan required
                    </span>
                  ) : null}
                  {isLocked ? (
                    <span className={styles.reportLockedBadge}>
                      Locked
                    </span>
                  ) : null}
                </div>
              </button>
            </li>
          );
        })}

        {/* TikTok Ads — Coming Soon */}
        <li className={`${styles.reportItem} ${styles.reportItem_tiktok || ""}`}>
          <div className={`${styles.reportTile} ${styles.reportTileComingSoon}`}>
            <div className={styles.reportContent}>
              <span className={styles.reportTitle}>TikTok Ads Report</span>
              <div className={styles.reportActions}>
                <span className={styles.reportBadge} aria-hidden="true">
                  <Music2 className={styles.badgeIcon} size={18} strokeWidth={2.1} />
                </span>
              </div>
              <span className={styles.comingSoonBadge}>Coming Soon</span>
            </div>
          </div>
        </li>
      </ul>

      {/* Upgrade / Paywall Modal */}
      {upgradeModalReport && (
        <UpgradeModal
          reportType={upgradeModalReport}
          onClose={() => setUpgradeModalReport(null)}
        />
      )}
    </section>
  );
};

export default ReportsPage;
